import { useForm, FormProvider } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { MembersFiltersSchema, MembersFiltersValues } from 'schemas/MembersFilters'
import Menu from '@ui-components-3/ui/lib/components/Menu'
import BarsFilterIcon from '@ui-components-3/icons/lib/solid/BarsFilter'
import useId from '@ui-components-3/ui/lib/hooks/useId'
import { useCallback, useEffect, useMemo, useState } from 'react'
import classNames from '@ui-components-3/ui/lib/utils/classNames'
import FormInput from 'components/common/hook-form/FormInput'
import FormDateTimePicker from 'components/common/hook-form/FormDateTimePicker'

type RenderFormProps = {
  id: string
  onSubmit: (values: MembersFiltersValues) => void
  defaultValues?: Partial<MembersFiltersValues>
}

const RenderForm = ({ id, onSubmit, defaultValues }: RenderFormProps) => {
  const methods = useForm({
    resolver: yupResolver(MembersFiltersSchema),
    mode: 'onSubmit',
    defaultValues,
  })

  const { handleSubmit, reset } = methods

  useEffect(() => {
    reset(defaultValues)
  }, [defaultValues, reset])

  return (
    <FormProvider {...methods}>
      <form className="grid grid-cols-1 sm:grid-cols-2 gap-5 p-4 bg-white" onSubmit={handleSubmit(onSubmit)} id={id}>
        <FormInput label="First Name" name="firstName" />
        <FormInput label="Last Name" name="lastName" />
        <FormInput label="Preferred Name" name="firstNamePreferred" />
        <FormDateTimePicker name="dob" label="Date of Birth" />
      </form>
    </FormProvider>
  )
}

type MembersFiltersFormProps = {
  currentFilters: Partial<MembersFiltersValues>
  onApply: (filters: MembersFiltersValues) => void
  onClear: () => void
}

const MembersFiltersForm = ({ currentFilters, onApply, onClear }: MembersFiltersFormProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const formId = useId('filters')
  const onSubmit = useCallback(
    (values) => {
      onApply?.(values)
      setIsOpen(false)
    },
    [onApply],
  )

  const onClearClick = useCallback(() => {
    onClear?.()
    setIsOpen(false)
  }, [onClear])

  const currentFiltersCount = useMemo(
    () => Object.values(currentFilters || {}).filter(Boolean).length,
    [currentFilters],
  )

  const defaultValues = useMemo(() => MembersFiltersSchema.cast(currentFilters), [currentFilters])

  return (
    <Menu
      className={classNames('btn self-end gap-3 mb-4', currentFiltersCount ? 'btn-neutral' : 'btn-neutral-outlined')}
      type="button"
      portal
      items={[]}
      opened={isOpen}
      onOpenChange={setIsOpen}
      prefix={<RenderForm id={formId} onSubmit={onSubmit} defaultValues={defaultValues} />}
      suffix={
        <div className="pt-2 px-4 pb-4 flex flex-col gap-4 bg-white">
          <hr />
          <div className="flex justify-between items-center gap-4">
            <button type="button" className="btn btn-primary-borderless flex-1" onClick={onClearClick}>
              Clear All
            </button>
            <button type="submit" form={formId} className="btn btn-primary flex-1">
              Apply
            </button>
          </div>
        </div>
      }
      dirtyZ={10000}
    >
      <BarsFilterIcon aria-hidden="true" />
      Filters {currentFiltersCount ? `(${currentFiltersCount})` : ''}
    </Menu>
  )
}
export default MembersFiltersForm
